import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { Sun, Moon } from 'lucide-react';
import api from '../../services/api';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import { getUserFacingError } from '../../services/errorMessage';

const ForgotPassword = () => {
  const { theme, isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');

    setLoading(true);

    try {
      const response = await api.post('/auth/request-password-reset', { email });
      setInfo(response.data.message || 'If an account exists for that email, a reset link has been sent.');
    } catch (err) {
      setError(getUserFacingError(err, 'Unable to process your request right now.'));
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      position: 'relative',
      background: isDark
        ? 'radial-gradient(circle at 18% 18%, #1f2937 0%, transparent 36%), radial-gradient(circle at 86% 90%, #0f766e 0%, transparent 38%), #0b1220'
        : 'radial-gradient(circle at 12% 22%, #dcfce7 0%, transparent 35%), radial-gradient(circle at 90% 80%, #cffafe 0%, transparent 38%), #f8fafc',
    },
    themeToggle: {
      position: 'absolute',
      top: '24px',
      right: '24px',
      width: '36px',
      height: '36px',
      borderRadius: '8px',
      backgroundColor: theme.colors.backgroundSecondary,
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s',
      color: theme.colors.text,
      zIndex: 2,
      backdropFilter: 'blur(4px)',
    },
    card: {
      width: '100%',
      maxWidth: '470px',
      borderRadius: '16px',
      border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.cardBg,
      boxShadow: '0 18px 32px rgba(15, 23, 42, 0.12)',
    },
    body: {
      padding: '24px',
    },
    title: {
      fontSize: '28px',
      fontWeight: '700',
      color: theme.colors.text,
      marginBottom: '8px',
    },
    subtitle: {
      fontSize: '14px',
      color: theme.colors.textSecondary,
      marginBottom: '22px',
      lineHeight: '1.6',
    },
    error: {
      padding: '12px',
      borderRadius: '6px',
      backgroundColor: theme.colors.error + '20',
      color: theme.colors.error,
      fontSize: '14px',
      marginBottom: '16px',
    },
    info: {
      padding: '12px',
      borderRadius: '6px',
      backgroundColor: theme.colors.primary + '12',
      color: theme.colors.primary,
      fontSize: '14px',
      marginBottom: '16px',
    },
    footer: {
      marginTop: '16px',
      textAlign: 'center',
      fontSize: '14px',
      color: theme.colors.textSecondary,
    },
    link: {
      color: theme.colors.primary,
      cursor: 'pointer',
      fontWeight: '500',
      textDecoration: 'underline',
      textUnderlineOffset: '2px',
    },
  };

  return (
    <div style={styles.container}>
      <button
        style={styles.themeToggle}
        onClick={toggleTheme}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = theme.colors.backgroundTertiary;
          e.currentTarget.style.transform = 'scale(1.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = theme.colors.backgroundSecondary;
          e.currentTarget.style.transform = 'scale(1)';
        }}
        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {isDark ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      <Card cardStyle={styles.card} bodyStyle={styles.body}>
        <h1 style={styles.title}>Forgot Password</h1>
        <p style={styles.subtitle}>
          Enter your account email and we will send a reset link to create a new password.
        </p>

        <form onSubmit={handleSubmit}>
          {error && <div style={styles.error}>{error}</div>}
          {info && <div style={styles.info}>{info}</div>}

          <Input
            type="email"
            name="email"
            label="Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
          />

          <Button type="submit" fullWidth disabled={loading}>
            {loading ? 'Sending reset link...' : 'Send Reset Link'}
          </Button>
        </form>

        <div style={styles.footer}>
          Remembered your password?{' '}
          <span style={styles.link} onClick={() => navigate('/login')}>
            Back to sign in
          </span>
        </div>
      </Card>
    </div>
  );
};

export default ForgotPassword;
